/**
 * <s-product-image-zoom> — PDP image zoom preloader and photo stepping.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus, and lets shoppers move
 * between enlarged photos with the arrow buttons, the keyboard arrow keys or a swipe.
 * Only photos currently shown in the gallery (after the colour filter) are stepped through.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 * Optional markup inside each dialog:
 *   [data-zoom-prev] / [data-zoom-next] — step buttons.
 *   [data-zoom-count]                   — "2 of 7" label; template from data-count-label ([current], [total]).
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #touchX = null;
  #touchY = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleStepClick, opts);
    this.addEventListener('keydown', this.#handleKey, opts);
    this.addEventListener('touchstart', this.#handleTouchStart, { ...opts, passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { ...opts, passive: true });
    this.addEventListener('toggle', this.#handleToggle, { ...opts, capture: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source.getAttribute('commandfor'));
  };

  #openDialogFrom(target) {
    if (!(target instanceof Element)) return null;
    const dialog = target.closest('dialog');
    return dialog && dialog.open && this.contains(dialog) ? dialog : null;
  }

  #handleStepClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-zoom-prev], [data-zoom-next]');
    const dialog = this.#openDialogFrom(button);
    if (!button || !dialog) return;
    event.preventDefault();
    this.#step(dialog, button.hasAttribute('data-zoom-next') ? 1 : -1);
  };

  #handleKey = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const dialog = this.#openDialogFrom(event.target);
    if (!dialog) return;
    event.preventDefault();
    const forward = event.key === 'ArrowRight' ? 1 : -1;
    this.#step(dialog, document.dir === 'rtl' ? -forward : forward);
  };

  #handleTouchStart = (event) => {
    if (!this.#openDialogFrom(event.target) || event.touches.length !== 1) {
      this.#touchX = null;
      return;
    }
    this.#touchX = event.touches[0].clientX;
    this.#touchY = event.touches[0].clientY;
  };

  #handleTouchEnd = (event) => {
    if (this.#touchX === null) return;
    const dialog = this.#openDialogFrom(event.target);
    const touch = event.changedTouches[0];
    const dx = touch.clientX - this.#touchX;
    const dy = touch.clientY - this.#touchY;
    this.#touchX = null;
    if (!dialog || Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    this.#step(dialog, dx < 0 ? 1 : -1);
  };

  #handleToggle = (event) => {
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.open) this.#updateCount(dialog);
  };

  /** Dialogs for the photos currently visible in the gallery, in gallery order. */
  #visibleDialogs() {
    const triggers = this.querySelectorAll('[data-zoom-preload][commandfor]');
    const dialogs = [];
    for (const trigger of triggers) {
      if (trigger.closest('[hidden]')) continue;
      const dialog = document.getElementById(trigger.getAttribute('commandfor'));
      if (dialog instanceof HTMLDialogElement) dialogs.push(dialog);
    }
    return dialogs;
  }

  #step(current, direction) {
    const dialogs = this.#visibleDialogs();
    if (dialogs.length < 2) return;
    let index = dialogs.indexOf(current);
    if (index < 0) index = 0;
    const next = dialogs[(index + direction + dialogs.length) % dialogs.length];
    this.#preload(next.id);
    // Open the next photo before closing the current one so the page never flashes between them.
    next.showModal();
    current.close();
    this.#updateCount(next);
    const after = dialogs[(dialogs.indexOf(next) + direction + dialogs.length) % dialogs.length];
    this.#preload(after.id);
  }

  #updateCount(dialog) {
    const dialogs = this.#visibleDialogs();
    const index = dialogs.indexOf(dialog);
    const single = index < 0 || dialogs.length < 2;
    // Hide the arrows when the colour filter leaves a single photo.
    for (const button of dialog.querySelectorAll('[data-zoom-prev], [data-zoom-next]')) {
      button.hidden = single;
    }
    const label = dialog.querySelector('[data-zoom-count]');
    if (!label) return;
    label.hidden = single;
    if (single) return;
    const template = this.dataset.countLabel || '[current] of [total]';
    label.textContent = template.replace('[current]', String(index + 1)).replace('[total]', String(dialogs.length));
  }

  #preload(dialogId) {
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
